import swal from 'sweetalert';
// Caminhos relativos, não os aliases do rspack: o Api.js importa este módulo, e os testes
// unitários do SPA rodam no jest, que não conhece `@/` nem `shared/`.
import { clearStorageKeepingDevice, getSessionId, isLoggingOut } from '../Auth';
import { SOCKET_AUTH_FAILED_CODE, SOCKET_AUTH_UNAVAILABLE_CODE } from '../../shared/constants/socket-auth';

/**
 * Sessão expirada: avisa, limpa o storage e recarrega para a tela de login.
 *
 * Centralizado porque existem dois caminhos que chegam aqui — o 401 das chamadas
 * HTTP, no interceptor do `Api.js`, e a recusa de handshake dos sockets — e eles
 * precisam terminar no mesmo lugar. A confiança do dispositivo no 2FA é um cookie
 * HttpOnly e não é tocada, então relogar num dispositivo confiável não dispara OTP.
 */

/**
 * Vários sockets podem falhar no mesmo instante (transcrição e chat na tela de
 * teleconsulta). Sem esta trava o usuário levaria um swal por socket e um reload
 * competindo com o outro.
 */
let alreadyHandling = false;

/** Janela para o `beforeunload` cancelar a navegação antes de rearmarmos a trava. */
const RELOAD_GRACE_MS = 5000;

/**
 * Sinaliza para o próximo carregamento que a sessão expirou.
 *
 * Vai no `sessionStorage` de propósito: ele sobrevive ao reload e o
 * `clearStorageKeepingDevice` só mexe no `localStorage`. É assim que a mensagem
 * chega na tela de login, onde dá para ler com calma — em vez de piscar por um
 * instante na tela que está sendo descartada.
 */
const SESSION_EXPIRED_FLAG = 'SessionExpired';

/**
 * Mostra o aviso quando o carregamento veio de uma sessão expirada. Consome o
 * sinal, então recarregar a tela de login de novo não repete a mensagem.
 */
export function consumeSessionExpiredNotice(): boolean {
	try {
		if (window.sessionStorage.getItem(SESSION_EXPIRED_FLAG) === null) return false;
		window.sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
		return true;
	} catch {
		return false;
	}
}

/** Sessão com que uma requisição axios saiu (carimbo do `attachClientId` em `error.config`). */
export function sessionIdOf(config: { sessionId?: unknown } | undefined): string | null {
	return typeof config?.sessionId === 'string' && config.sessionId.length > 0 ? config.sessionId : null;
}

/**
 * @param failedSessionId a sessão com que a requisição recusada saiu. O cookie de sessão e o
 * localStorage são das abas todas: se o storage já guarda OUTRA sessão, outra aba saiu e entrou de
 * novo, e esta aba levou o 401 de uma requisição que ainda era da sessão antiga. Limpar aqui
 * apagaria a sessão nova da outra aba; basta recarregar, e esta aba volta já dentro dela.
 */
export function handleSessionExpired(failedSessionId?: string | null): void {
	// Durante o "Sair" a sessão já foi encerrada de propósito: quem navega é o logout.
	if (alreadyHandling || isLoggingOut()) return;
	alreadyHandling = true;

	const currentSessionId = getSessionId();
	if (failedSessionId && currentSessionId && currentSessionId !== failedSessionId) {
		window.location.reload();
		window.setTimeout(() => {
			alreadyHandling = false;
		}, RELOAD_GRACE_MS);
		return;
	}

	clearStorageKeepingDevice();

	try {
		window.sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
	} catch {
		// Storage indisponível: perde-se só o aviso na tela de login, não o logout.
	}

	// Diálogo disparado sem esperar por ele. NÃO pendure nada no `.then` desta
	// promise: o `swal` do sweetalert guarda UM par resolve/reject no módulo e o
	// sobrescreve a cada chamada, então qualquer outro diálogo aberto no meio
	// rouba o resolve e esta promise nunca se estabelece. Como o interceptor do
	// `Api.js` não rejeita no 401, o `.then` da tela chamadora roda logo em
	// seguida e abrir um `swal` ali é o caso comum, não o raro. Este aviso existe
	// para o caso de a navegação abaixo ser cancelada; quando ela acontece, quem
	// informa é a tela de login, pelo `SESSION_EXPIRED_FLAG`.
	void swal('Sessão expirada', 'Por motivos de segurança é necessário que você faça o login novamente.', 'error');

	window.location.reload();

	// A navegação pode ser CANCELADA por um `beforeunload` — o LaudoModal registra
	// um quando há alteração não salva, e é a tela que mais fica aberta
	// atravessando a validade do token (até o fim do dia). Sem rearmar, a aba ficaria surda a
	// todo 401 seguinte: sem aviso, sem limpeza, sem redirecionamento. Se a
	// navegação acontecer, este timer morre junto com o documento.
	window.setTimeout(() => {
		alreadyHandling = false;
	}, RELOAD_GRACE_MS);
}

/**
 * Distingue "token recusado" de "servidor fora do ar" num `connect_error`.
 *
 * Sem isto os dois casos são indistinguíveis, e como os sockets usam
 * `reconnectionAttempts: Infinity` a sessão expirada viraria tentativa de
 * reconexão eterna, em silêncio.
 */
export function isSocketAuthError(error: unknown): boolean {
	return (error as { data?: { code?: string } })?.data?.code === SOCKET_AUTH_FAILED_CODE;
}

/**
 * O servidor não conseguiu validar a sessão (banco fora), sem recusá-la. Não é
 * expiração: quem recebe religa depois de um tempo. O Socket.IO destrói o socket em todo
 * erro de middleware, então sem religar à mão ele não volta sozinho.
 */
export function isSocketAuthUnavailable(error: unknown): boolean {
	return (error as { data?: { code?: string } })?.data?.code === SOCKET_AUTH_UNAVAILABLE_CODE;
}

/** Espera antes de religar o socket quando a validação da sessão falhou no servidor. */
export const SOCKET_AUTH_UNAVAILABLE_RETRY_MS = 5000;
